// Libraries
import React, { Component } from 'react';
import __OPTIONS from './option';

/**
 * Map
 *
 * Re-usable component that display the Google Map marker
 *
 * @since 0.5
 *
 * @param props
 * @return {jsx}
 *
 */
class Map extends Component {
    constructor(props) {
        super(props);

        this.state = {
            map: null,
            markers: [],
            mapZoom: 6,
            userLocation: null,
            userCountry: null,
            userRegion: null,
        };

        this.mapInit = this.mapInit.bind(this);
        this.dropMarker = this.dropMarker.bind(this);
        this.userLocationUpdateMap = this.userLocationUpdateMap.bind(this);
        this.requestUserLocation = this.requestUserLocation.bind(this);
        this.fetchGeolocationAPI = this.fetchGeolocationAPI.bind(this);
        this.reverseGeocode = this.reverseGeocode.bind(this);

        /**
         * CAA production API key
         *
         * See /wp-content/mu-plugins/caa/includes/MapAPI.php for more details.
         */
        this.googleAPIKey = 'AIzaSyDxjDnB3aMoW5GqNrdw2uQfZfcZAAQU40o';

        /**
         * PL WR development API key.
         *
         * See /wp-content/mu-plugins/caa/includes/MapAPI.php for more details.
         */
        // this.googleAPIKey = 'AIzaSyAnh4UY0BLf_tIYpR5WhZdV7OG-I4FPLA8';
    }

    mapInit(center, zoom) {
        const map = new google.maps.Map(document.getElementById('map-box'), {
            center: center,
            zoom: zoom,
            maxZoom: 16,
            disableDefaultUI: true,
            styles: __OPTIONS.styles,
        });

        this.setState({ map });

        return map;
    }

    dropMarker(markerCoordinates) {
        const { map, markers } = this.state;
        const currentMarkers = [];

        if (!map) {
            console.warn('Map instance is not initialized.');

            return;
        }

        const marker = new google.maps.Marker({
            position: markerCoordinates,
            map: map,
        });

        currentMarkers.push(marker);

        markers.forEach((marker) => {
            marker.setMap(null);
        });

        this.setState({ markers: currentMarkers });

        if (markerCoordinates) {
            marker.setMap(map);
        } else {
            marker.setMap(null);
        }

        return marker;
    }

    userLocationUpdateMap(lat, lng, country, region) {
        const { mapCoordinates } = this.props;
        const clubRegion = mapCoordinates?.province?.[0]?.toLowerCase();

        if (country === 'CA' && clubRegion === region) {
            this.setState(
                {
                    userLocation: { lat, lng },
                    userCountry: country,
                    userRegion: region,
                },
                () => {
                    const { map, userLocation } = this.state;

                    if (map) {
                        map.setCenter(userLocation);
                        map.setZoom(14);
                    }
                }
            );
        } else {
            console.warn("User's region does not match current club.");
        }
    }

    async reverseGeocode(lat, lng) {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${this.googleAPIKey}`;

        try {
            const response = await fetch(url, {
                method: 'POST',
            });

            if (!response.ok) {
                throw new Error(`Geocoding API error: ${response.statusText}`);
            }

            const data = await response.json();
            const addressComponents = data.results[0]?.address_components;

            if (addressComponents) {
                const country = addressComponents.find((comp) =>
                    comp.types.includes('country')
                )?.short_name;

                const region = addressComponents.find((comp) =>
                    comp.types.includes('administrative_area_level_1')
                )?.short_name;

                return { country, region };
            }
        } catch (error) {
            console.error('Error fetching geocode data:', error);

            return { country: null, region: null };
        }
    }

    async fetchGeolocationAPI() {
        const url = `https://www.googleapis.com/geolocation/v1/geolocate?key=${this.googleAPIKey}`;

        try {
            const response = await fetch(url, {
                method: 'POST',
            });

            if (!response.ok) {
                throw new Error(
                    `Geolocation API error: ${response.statusText}`
                );
            }

            const data = await response.json();
            const { lat, lng } = data.location;
            const locationInfo = await this.reverseGeocode(lat, lng);

            if (locationInfo) {
                this.userLocationUpdateMap(
                    lat,
                    lng,
                    locationInfo.country,
                    locationInfo.region
                );
            }
        } catch (error) {
            console.error(
                'Error fetching location from Geolocation API:',
                error
            );
        }
    }

    async requestUserLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude: lat, longitude: lng } = position.coords;
                    const locationInfo = await this.reverseGeocode(lat, lng);

                    if (locationInfo) {
                        this.userLocationUpdateMap(
                            lat,
                            lng,
                            locationInfo.country,
                            locationInfo.region
                        );
                    }
                },
                () => {
                    console.warn('Location access denied by user.');
                }
            );
        } else {
            console.warn('Geolocation is not supported by this browser.');
            this.fetchGeolocationAPI();
        }
    }

    componentDidMount() {
        let mapDetails = {
            location: { lat: 53.9332706, lng: -116.5765035 },
            zoom: this.state.mapZoom,
        };

        if (this.props.mapCoordinates) {
            mapDetails.location = this.props.mapCoordinates.location;
            mapDetails.zoom = this.props.mapCoordinates.zoom;
        }

        this.mapInit(mapDetails.location, mapDetails.zoom);

        if (this.props.markerCoordinates) {
            this.dropMarker(this.props.markerCoordinates);
        }

        this.requestUserLocation();
    }

    componentDidUpdate(prevProps, prevState) {
        const { map, mapZoom, userLocation } = this.state;
        const { markerCoordinates, mapCoordinates } = this.props;

        if (userLocation && userLocation !== prevState.userLocation) {
            map.setCenter(userLocation);
        }

        if (
            markerCoordinates !== prevProps.markerCoordinates &&
            markerCoordinates != null
        ) {
            map.setCenter(markerCoordinates);
            map.setZoom(mapZoom);

            this.dropMarker(markerCoordinates);

            const bounds = new google.maps.LatLngBounds();

            bounds.extend(markerCoordinates);
            map.fitBounds(bounds);
            map.panToBounds(bounds);
        }

        if (
            markerCoordinates !== prevProps.markerCoordinates &&
            markerCoordinates == null
        ) {
            map.setCenter(mapCoordinates.location);
            map.setZoom(mapCoordinates.zoom);

            this.dropMarker(map, null);
        }
    }

    render() {
        return <div id="map-box" className="map-window"></div>;
    }
}

export default Map;
